import { useState, useEffect } from 'react'
import { AxiosError } from 'axios'

import * as UploadService from 'services/uploadService'
import useImportProgress from 'components/Projects/components/Import/hooks/useImportProgress'
import { projectData } from 'actions'
import { useAction } from 'hooks/utils'
import useAbortController from 'components/Projects/components/Import/hooks/useAbortController'
import { NW_JS_APP_PROFILES } from 'enums'
import { LOCAL_STORAGE_PROJECT_IMPORT_CREATE_FOLDER } from 'constant'

export type Options = {
  createFolder?: boolean
  fps?: number
}

type Props = {
  onError: (message: string) => void
}

const getInitialCreateFolder = () => {
  const savedValue = localStorage.getItem(LOCAL_STORAGE_PROJECT_IMPORT_CREATE_FOLDER)
  return savedValue === null ? true : savedValue === 'true'
}

function useUpload({ onError }: Props) {
  const [ sessionId, setSessionId ] = useState('')
  const [ folderId, setFolderId ] = useState<string | null>(null)
  const [ settings, _setSettings ] = useState<Options>(() => ({
    createFolder: getInitialCreateFolder(),
  }))
  const addProject = useAction(projectData.addProject)

  const controller = useAbortController()
  const [ progress, setProgress, stage, setStage ] = useImportProgress(sessionId, controller, onError)

  useEffect(() => {
    if (stage === 'ready') {
      addProject(sessionId)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ stage ])

  const setSettings = (settings: Options) => {
    if (typeof settings.createFolder === 'boolean') {
      localStorage.setItem(
        LOCAL_STORAGE_PROJECT_IMPORT_CREATE_FOLDER,
        String(settings.createFolder)
      )
    }
    _setSettings(x => ({ ...x, ...settings }))
  }

  const stopImport = () => {
    if (stage === 'stop' || stage === 'ready') return
    setStage('stop')
    controller.abort()

    UploadService.cancelImport(`api/upload/project?id=${sessionId}`)
  }

  const uploadService = async (uploadFile: File) => {
    let response = null
    setFolderId(null)
    const isDesktop = NW_JS_APP_PROFILES.includes(__APP_PROFILE__ as NwJsProfileType)
    const file = uploadFile as File & { path: string }
    const body = isDesktop ? { absPathName: file.path } : { name: file.name, size: file.size }

    try {
      response = await UploadService.createUploadSession(body, 'project', { ...settings })
    } catch (err) {
      // in case of attempt to load file larger than 2Gb, creation of upload
      // session will fail with code 507, so axios throws an exception
      response = (err as AxiosError).response
      setStage('stop')
    }

    if (response?.data.id !== undefined) {
      const { id, folderId: createdFolderId } = response.data
      setSessionId(id)
      setFolderId(createdFolderId ?? null)

      const onProgress = async () => {
        try {
          const { progress, stage } = await UploadService.getProjectProgress(id, controller)
          setProgress(progress)
          setStage(stage)
          if (stage === 'upload') {
            setTimeout(onProgress, 100)
          }
        } catch (e) {
          if (e instanceof Error) {
            if (e.name.includes('AbortError')) {
              setStage('stop')
            }
            if (e.message.includes('ImportProjectManager')) {
              setStage('error')
              onError(e.message)
              controller.abort()
              console.error('Import error', e.message)
            }
          }
        }
      }

      const onFileUploaded = () => {
        setStage(x => x === 'upload' ? 'unpack' : x)
      }

      if (!isDesktop) {
        const request = (options: UploadService.RequestParams) => UploadService
          .uploadFileRequest({ ...options, type: 'project' })

        UploadService.uploadFile({ id,
          file,
          request,
          onFileUploaded,
          onError: () => { setStage('stop') },
          controller })
        setTimeout(onProgress, 100)
        setStage('upload')
      } else {
        setStage('upload')
        await onProgress()
        onFileUploaded()
      }
    }
  }

  return { sessionId,
    folderId,
    settings,
    setSettings,
    progress,
    stage,
    setStage,
    uploadCancel: stopImport,
    uploadService }
}

export default useUpload
